import type {
  ColDef,
  GridApi,
  ICellRendererParams,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-enterprise';
import Big from 'big.js';
import { get, isArray, isObject, sortBy } from 'lodash-es';
import type { ReactNode } from 'react';

import {
  ConnectionModeEnum,
  ConnectionStatusEnum,
  CustomerBalanceTransactionTypeEnum,
  ExposureStatusEnum,
  ModeEnum,
  QuoteStatusEnum,
  type ProductTypeEnum,
  type RFQProtocolEnum,
  type ReconStateEnum,
  type SideEnum,
} from '../../../types/types';
import type { Leaves } from '../../../utils';
import {
  extendedDurationComparator,
  formatPercent,
  getFilledBaseQuantity,
  getFilledCounterAmount,
  getFilledNotional,
  getOpenNotional,
  getProductGroupLabel,
  getProductType,
  getTotalNotional,
  getTypedKeys,
  percentToBps,
  prettyExtendedDuration,
  prettyName,
  productTypeToString,
  type AmountObject,
  type DateFnInput,
  type ProductGroup,
} from '../../../utils';
import { AgGridToggle } from '../../AgGrid';
import { Button } from '../../Button';
import { Flex, HStack } from '../../Core';
import { FormControlSizes } from '../../Form/types';
import { FormRowStatus, type FormRowGridItem } from '../../FormTable/types';
import { ICON_SIZES, Icon, IconName } from '../../Icons';
import { MarketColorDot } from '../../MarketColor';
import { QuoteSide, TWO_WAY_SIDE } from '../../Side';
import {
  AddressStatus,
  CareOrderStatus,
  ExposureStatus,
  QuoteStatus,
  ReconMismatchStatus,
  TradeStatus,
  TransferStatus,
} from '../../Status';
import { JITTooltip, Tooltip } from '../../Tooltip';
import { CustomerRolePillWrapper } from '../styles';
import { baseColumn } from './baseColumn';
import {
  amountObjectComparator,
  amountObjectValueFormatter,
  numericColumnComparator,
  stringColumnComparator,
} from './utils';

import type { CustomCellEditorProps } from 'ag-grid-react';
import { defineMessages } from 'react-intl';
import type { ToggleParams } from '..';
import type { MarketAccount } from '../../../contexts/MarketAccountsContext';
import { strategyTranslations } from '../../../hooks';
import { ReconStateEnumLabels, type Order, type Quote, type QuoteMarket } from '../../../types';
import type { AggregationMarket } from '../../../types/Aggregation';
import type { CustomerRole } from '../../../types/CustomerRole';
import type { CustomerUser } from '../../../types/CustomerUser';
import type { Security } from '../../../types/Security';
import type {
  AgGridButtonProps,
  AgGridFormattedNumberProps,
  AgGridIconButtonProps,
  AgGridSearchSelectDropdownProps,
} from '../../AgGrid/types';
import { InlineFormattedNumber } from '../../FormattedNumber';
import type { FormBlotterTableContextProps } from '../../FormTable';
import { IndicatorBadge, IndicatorBadgeSizes, IndicatorBadgeVariants } from '../../IndicatorBadge';
import type { SizeColumnParams } from './size.types';
import { toggle } from './toggle';
import type { ColDefFactory, Column } from './types';

const messages = defineMessages({
  transfer: {
    defaultMessage: 'Transfer',
    id: 'BlotterTable.columns.transfer',
  },
  withdraw: {
    defaultMessage: 'Withdraw',
    id: 'BlotterTable.columns.withdraw',
  },
  deposit: {
    defaultMessage: 'Deposit',
    id: 'BlotterTable.columns.deposit',
  },
  twoWay: {
    defaultMessage: '2-way',
    id: 'BlotterTable.columns.twoWay',
  },
  withdrawal: {
    defaultMessage: 'Withdrawal',
    id: 'BlotterTable.columns.withdrawal',
  },
  crypto: {
    defaultMessage: 'Crypto',
    id: 'BlotterTable.columns.crypto',
  },
  fiat: {
    defaultMessage: 'Fiat',
    id: 'BlotterTable.columns.fiat',
  },
});

/**
 * Given an array of strings, formats to one comma-concatenated string. The value of the cell is unchanged.
 */
export const textArray: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueFormatter: ({ value }: ValueFormatterParams<unknown, unknown>): string => {
    if (isArray(value)) {
      return value.join(', ');
    }
    return '';
  },
});

export const email: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  cellEditor: 'input',
  valueSetter: params => {
    if (column.field == null) {
      return false;
    }
    // Remove whitespaces
    params.data[column.field] = params.newValue.replace(/\s/g, '');
    return true;
  },
});

// Note that this coldef works with single roles. Confusingly though they are represented as [Role]
// TODO this column definition is coupled to Customer land and should be called "customerRole" and be moved out of kyoko column defs
export const role: ColDefFactory<Column> = column => {
  const customerRolesMapByName: Map<string, CustomerRole> | undefined = column.params?.customerRoles;
  if (customerRolesMapByName == null) {
    throw new Error('customerRolesMapByName cannot be null for the role column');
  }
  return {
    ...baseColumn(column),
    valueGetter: ({ data }: { data: CustomerUser }) => {
      if (data.Roles === undefined || data.Roles.length === 0) {
        return '';
      } else if (data.Roles.length === 1) {
        // When creating a new row, we have a string as the role instead of a CustomerRole object
        if (typeof data.Roles[0] === 'string') {
          return customerRolesMapByName?.get(data.Roles[0])?.DisplayName;
        }
        return data.Roles[0].DisplayName;
      } else {
        // This shouldn't happen, but we'll still handle it gracefully
        return data.Roles.map(role => role.DisplayName).join(', ');
      }
    },
    cellRenderer: ({ value, context }: ICellRendererParams) => {
      if (!value) {
        return '';
      }
      return <CustomerRolePillWrapper theme={context.current.theme}>{value}</CustomerRolePillWrapper>;
    },
    cellEditor: 'searchSelectDropdown',
    cellEditorPopup: true,
    suppressKeyboardEvent: params => params.event.key === 'Enter',
    cellEditorParams: (params: CustomCellEditorProps) => {
      if (customerRolesMapByName != null) {
        return {
          ...params,
          useSearchSelectParams: {
            getLabel: value => customerRolesMapByName?.get(value[0])?.DisplayName ?? '',
            // Each individual item must be represented as [name] where name is the Role
            items: [...customerRolesMapByName.keys()].map(name => [name]) || [],
          },
        } satisfies AgGridSearchSelectDropdownProps<string[]>;
      }
    },
  };
};

export const tradingMarketAccounts: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  cellRenderer: ({ data }: ICellRendererParams) => {
    const marketAccounts = column.params.marketAccountsByName;
    const getDisplayName = (name: string) => {
      if (marketAccounts.has(name)) {
        return marketAccounts.get(name).DisplayName;
      } else {
        return name;
      }
    };
    const accounts: Map<string, AggregationMarket> = data.Accounts;
    const filteredAccounts = Array.from(accounts.values())
      .filter(marketAccount => {
        return marketAccount.Mode === ModeEnum.Enabled;
      })
      .map<{
        key: string;
        displayName: string;
        market: string;
      }>(marketAccount => {
        const displayName = getDisplayName(marketAccount.MarketAccount || '');
        return {
          key: marketAccount.Market || displayName,
          displayName,
          market: marketAccount.Market ?? '',
        };
      })
      .filter(account => account.displayName !== '')
      .toSorted((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()));
    return (
      <Tooltip
        usePortal
        placement="auto"
        tooltip={
          <>
            {filteredAccounts.map((marketAccount, index) => {
              return (
                <Flex alignItems="center" key={marketAccount.key} justifyContent="flex-start">
                  <MarketColorDot market={marketAccount.market} />
                  <span style={{ marginLeft: '10px' }}>{marketAccount.displayName}</span>
                </Flex>
              );
            })}
          </>
        }
      >
        {filteredAccounts
          .map(marketAccount => {
            return marketAccount.displayName;
          })
          .join(', ')}
      </Tooltip>
    );
  },
});

export const cumBaseQty: ColDefFactory<Column<SizeColumnParams>> = column => ({
  ...baseColumn(column),
  type: 'numericColumn',
  cellRenderer: 'sizeColumn',
  valueGetter: ({ data, context }): AmountObject => {
    const security = context.current.securitiesBySymbol?.get(data.Symbol);
    return getFilledBaseQuantity(data, security);
  },
  valueFormatter: amountObjectValueFormatter,
  comparator: amountObjectComparator,
  headerClass: 'ag-right-aligned-header',
});

type PercentParams = AgGridFormattedNumberProps;
export const percent: ColDefFactory<Column<PercentParams>> = column => ({
  maxWidth: 240,
  ...baseColumn(column),
  type: 'numericColumn',
  minWidth: 70,
  headerClass: column.headerClass ?? 'ag-right-aligned-header',
  cellEditor: 'input',
  cellRenderer: 'formattedNumberColumn',
  cellRendererParams: { ...column.params, unit: '%' },
  valueParser: ({ oldValue, newValue }) => {
    const value = newValue?.trim();
    try {
      Big(value).div(100);
      return value;
    } catch (e) {
      return oldValue;
    }
  },
  valueGetter: params => {
    try {
      const value = params.colDef.field != null ? get(params.data, params.colDef.field) : null;
      return formatPercent(value);
    } catch (e) {
      return null;
    }
  },
  comparator: numericColumnComparator,
});

/** Grabs the product type off of the the security associated with your data. The security is retrieved using data.Symbol */
export const productType: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    valueGetter: ({ data, context }) => {
      if (data == null) {
        return '';
      }

      const symbol = column.field ? get(data, column.field) : data.Symbol;
      if (!symbol) {
        return '';
      }

      const security = context.current.securitiesBySymbol?.get(symbol);
      return getProductType(security);
    },
    valueFormatter: ({ value }: ValueFormatterParams<unknown, ProductGroup>) => {
      if (value == null) {
        return '';
      }
      return getProductGroupLabel(value);
    },
  };
};

/** Grabs the product type using the given field. Prettifies before rendering. */
export const productTypeField: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueFormatter: ({ value }: ValueFormatterParams<unknown, ProductTypeEnum | undefined>) => {
    return value ? productTypeToString(value) : '';
  },
});

export const filledNotional: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    type: 'numericColumn',
    minWidth: 70,
    maxWidth: 180,

    cellRenderer: 'sizeColumn',
    valueGetter: ({ data, context }): AmountObject => {
      const security = context.current.securitiesBySymbol?.get(data.Symbol);
      return getFilledNotional(data, security);
    },
    valueFormatter: amountObjectValueFormatter,
    comparator: amountObjectComparator,
    headerClass: 'ag-right-aligned-header',
  };
};

export const openNotional: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    type: 'numericColumn',
    minWidth: 70,
    maxWidth: 180,

    cellRenderer: 'sizeColumn',
    valueGetter: ({ data, context }): AmountObject => {
      const security = context.current.securitiesBySymbol?.get(data.Symbol);
      return getOpenNotional(data, security);
    },
    valueFormatter: amountObjectValueFormatter,
    comparator: amountObjectComparator,
    headerClass: 'ag-right-aligned-header',
  };
};

export const totalNotional: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    type: 'numericColumn',
    minWidth: 70,
    maxWidth: 180,

    cellRenderer: 'sizeColumn',
    valueGetter: ({ data, context }): AmountObject => {
      const security = context.current.securitiesBySymbol?.get(data.Symbol);
      return getTotalNotional(data, security);
    },
    valueFormatter: amountObjectValueFormatter,
    comparator: amountObjectComparator,
    headerClass: 'ag-right-aligned-header',
  };
};

export const filledCounterAmount: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    type: 'numericColumn',
    minWidth: 70,
    maxWidth: 180,

    cellRenderer: 'sizeColumn',
    valueGetter: ({ data, context }): AmountObject => {
      const security = context.current.securitiesBySymbol?.get(data.Symbol);
      return getFilledCounterAmount(data, security);
    },
    valueFormatter: amountObjectValueFormatter,
    comparator: amountObjectComparator,
    headerClass: 'ag-right-aligned-header',
  };
};

export const transferStatus: ColDefFactory<Column> = column => {
  return {
    initialWidth: 100,
    ...baseColumn(column),
    cellRenderer: (params: ICellRendererParams) => {
      const data = params.data;
      if (data == null) {
        return params.value;
      }
      return (
        // data.RejectText for CustomerBalanceTransaction messages
        <TransferStatus theme={params.context.current.theme} status={data.Status} text={data.Text || data.RejectText} />
      );
    },
  };
};

export const addressStatus: ColDefFactory<Column> = column => {
  return {
    initialWidth: 100,
    ...baseColumn(column),
    cellRenderer: (params: ICellRendererParams) => {
      const data = params.data;
      if (data == null) {
        return params.value;
      }
      return <AddressStatus theme={params.context.current.theme} status={data.Status} text={data.Text} />;
    },
  };
};

export const tradeStatus: ColDefFactory<Column> = column => ({
  initialWidth: 128,
  ...baseColumn(column),
  field: 'TradeStatus',
  colId: 'TradeStatus',
  cellRenderer: (params: ICellRendererParams) => {
    if (params.data == null) {
      return params.value;
    }
    return (
      <TradeStatus
        tradeStatus={params.data.TradeStatus}
        text={params.data.Text}
        iconPlacement="left"
        theme={params.context.current.theme}
      />
    );
  },
});

export const careOrderStatus: ColDefFactory<Column> = column => ({
  initialWidth: 128,
  field: 'status',
  ...baseColumn(column),
  cellRenderer: (params: ICellRendererParams) => {
    if (params.data == null) {
      return params.value;
    }
    return <CareOrderStatus status={params.value} text={params.data.Text} iconPlacement="left" />;
  },
});

export type QuoteStatusColumnParams<T extends object = any> = {
  tooltipField?: Leaves<T>;
  rfqProtocolField?: Leaves<T>;
};

type QuoteStatusValue =
  | {
      status: QuoteStatusEnum;
      text?: string;
      rfqProtocol?: RFQProtocolEnum;
    }
  | undefined;

export const quoteStatus: ColDefFactory<Column<QuoteStatusColumnParams>> = column => ({
  ...baseColumn(column),
  valueGetter: (params: ValueGetterParams<unknown>): QuoteStatusValue => {
    if (!params.colDef.field || !params.data) {
      return undefined;
    }

    const status: QuoteStatusEnum | undefined = get(params.data, params.colDef.field);
    if (!status) {
      return undefined;
    }

    return {
      status,
      text: column.params?.tooltipField ? get(params.data, column.params.tooltipField) : undefined,
      rfqProtocol: column.params?.rfqProtocolField ? get(params.data, column.params.rfqProtocolField) : undefined,
    };
  },
  cellRenderer: (params: ICellRendererParams<unknown, QuoteStatusValue>) => {
    if (!params.value) {
      return '';
    }

    return (
      <QuoteStatus
        quoteStatus={params.value.status}
        rfqProtocol={params.value.rfqProtocol}
        iconPlacement="left"
        tooltipText={params.value.text}
        theme={params.context.current.theme}
      />
    );
  },
  valueFormatter: (params: ValueFormatterParams<unknown, QuoteStatusValue>) => {
    if (!params.value) {
      return '';
    }

    return params.value.status;
  },
  filterValueGetter: (params: ValueGetterParams<unknown>) => {
    if (!params.colDef.field || !params.data) {
      return undefined;
    }

    return get(params.data, params.colDef.field);
  },
});

export const reconMismatchStatus: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  cellRenderer: ({ value }: ICellRendererParams<unknown, ReconStateEnum | undefined>) => {
    if (!value) {
      return null;
    }
    return <ReconMismatchStatus status={value} />;
  },
  valueFormatter: ({ value }: ValueFormatterParams<unknown, ReconStateEnum | undefined>) => {
    if (!value) {
      return '';
    }

    return ReconStateEnumLabels[value];
  },
});

export const transactionType: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    cellRenderer: (params: ICellRendererParams) => {
      const value = params.context.current.intl.formatCell(params, messages);
      return (
        <HStack alignItems="center" gap="spacing8">
          <Icon
            icon={params.value === CustomerBalanceTransactionTypeEnum.Deposit ? IconName.Download : IconName.Upload}
            size={ICON_SIZES.LARGE}
            style={{ marginRight: params.context.current.theme?.spacing4 }}
          />
          {value}
        </HStack>
      );
    },
  };
};

export const addressRoutingType: ColDefFactory<Column> = column => {
  return {
    ...baseColumn(column),
    cellRenderer: (params: ICellRendererParams) => params.context.current.intl.formatCell(params, messages),
  };
};

export const processStep: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  colId: 'ProcessStep',
  cellRendererParams: column.params,
  cellRenderer: 'processStepColumn',
});

export const quoteValue: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  type: 'numericColumn',
  minWidth: 70,
  maxWidth: 180,

  cellRenderer: 'priceColumn',
  cellRendererParams: column.params,
  valueGetter: ({ data, context }) => {
    const { TradedPx, TradedAmt, Symbol, Currency } = data;
    const { QuoteCurrency } = (column.params?.securities ?? context.current.securitiesBySymbol)?.get(Symbol) ?? {};
    const isCcy = QuoteCurrency === Currency;
    const value = !isCcy
      ? TradedAmt
      : Big(TradedAmt || 0)
          .times(TradedPx || 1)
          .toFixed();
    return value;
  },
  comparator: numericColumnComparator,
});

/** Null means no side (different from 2-way) */
type QuoteSideValue = SideEnum | typeof TWO_WAY_SIDE | null;
export const quoteSide: ColDefFactory<Column> = column => ({
  initialWidth: 60,
  ...baseColumn(column),
  valueGetter: ({ data }: ValueGetterParams<unknown>): QuoteSideValue => {
    const side = column.field ? get(data, column.field) : undefined;
    // You can use this column if you are ever returning "2-way" as a value or not, both are okay
    // If the resolved side is undefined, it is interpreted as 2-way (which is how a Quote works)
    // null is a valid value, interpreted as "do not render anything"
    return side === undefined ? TWO_WAY_SIDE : side;
  },
  cellRenderer: ({ value, context }: ICellRendererParams<unknown, QuoteSideValue>) => {
    return <QuoteSide side={value} />;
  },
});

export const strategy: ColDefFactory<Column> = column => ({
  initialWidth: 96,
  ...baseColumn(column),
  valueGetter: ({ data }: ValueGetterParams<unknown>) => {
    const field = column.field;
    if (!field || !data) {
      return undefined;
    }

    const strategyName: string | undefined = get(data, field);
    if (strategyName) {
      return strategyName;
    }

    // For orders. If we weren't able to resolve any specific strategy, this might be an RFQ Order. In that case,
    // there'll be an RFQID. If we find that, just show RFQ as the strategy.
    if ((data as Order).RFQID != null) {
      return 'RFQ';
    }

    return undefined;
  },
  valueFormatter: (params: ValueFormatterParams<unknown, string | undefined>) => {
    return (
      params.context.current.intl.formatCell(params, strategyTranslations, params.context.current.strategiesByName) ??
      ''
    );
  },
});

export const filler: ColDefFactory<Column> = column => ({
  ...baseColumn({ ...column, exportable: false }),
  headerName: '',
  headerClass: 'no-handle',
  suppressColumnsToolPanel: true,
  suppressFiltersToolPanel: true,
  suppressHeaderMenuButton: true,
  flex: 1,
});

export const exposureUtilization: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  cellRenderer: 'meterColumn',
  cellRendererParams: {
    showInitialAnimation: false,
    ...column.params,
  },
  menuTabs: [],
  minWidth: 140,
  initialWidth: 180,
  maxWidth: 480,
  flex: 1,
  valueGetter: ({ data, context }: ValueGetterParams<any>) => {
    if (!data.Exposure || !data.ExposureLimit) {
      return { value: null };
    }
    const limit = Big(data.ExposureLimit);
    const exposure = Big(data.Exposure);

    if (limit.eq(0) || exposure.eq(0)) {
      return { value: '0' };
    }

    const value = Big(exposure).div(limit).toFixed();
    if (context.current.theme == null) {
      return { value };
    }
    const { colorTextPositive, colorTextWarning } = context.current.theme;
    const color = Big(value).gte(1) ? colorTextWarning : colorTextPositive;
    return {
      value,
      color,
    };
  },
});

// To be used with OMSQuote4401 data
export const quoteMarkets: ColDefFactory<Column> = column =>
  ({
    ...baseColumn(column),
    valueGetter: ({ data, colDef }) => {
      if (!data) {
        return [];
      }
      const markets = data[colDef.field as keyof Quote] as QuoteMarket[];
      const quoteStatus = data.QuoteStatus;
      const isQuoteDone = quoteStatus === QuoteStatusEnum.Filled;

      let value = markets;
      if (column.params?.showFilledOnly && isQuoteDone) {
        value = markets.filter(m => m.QuoteStatus === QuoteStatusEnum.Filled);
      }
      return value;
    },
    valueFormatter: ({ value, context }: ValueFormatterParams<Quote, QuoteMarket[]>) => {
      if (value == null) {
        return '';
      }
      return sortBy(value.map(({ Market }) => context.current.marketDisplayNameByName?.get(Market) ?? Market))?.join(
        ', '
      );
    },
  }) as ColDef<Quote>;

export const marketColor: ColDefFactory<Column> = column => ({
  initialWidth: 32,
  ...baseColumn(column),
  cellRendererParams: column.params,
  headerName: '',
  cellRenderer: ({ value }: ICellRendererParams) => {
    return (
      <Flex alignItems="center">
        <MarketColorDot market={value} />
      </Flex>
    );
  },
});

export const button: ColDefFactory<Column<AgGridButtonProps & { alignment?: ColDef['type'] }>> = column => ({
  ...baseColumn({ ...column, exportable: false }),
  headerName: (typeof column.title === 'string' && column.title) || '',
  menuTabs: [],
  cellRenderer: 'buttonColumn',
  cellRendererParams: column.params,
  type: column.params?.alignment,
});

export const iconButton: ColDefFactory<Column<AgGridIconButtonProps & { alignment?: ColDef['type'] }>> = column => ({
  suppressColumnsToolPanel: true,
  ...baseColumn({ ...column, exportable: false }),
  headerName: '',
  menuTabs: [],
  cellRenderer: 'iconButtonColumn',
  cellRendererParams: column.params,
  type: column.params?.alignment,
});

export const orderButton: ColDefFactory<Column> = column => ({
  ...baseColumn({ ...column, exportable: false }),
  headerName: column?.params?.side,
  menuTabs: [],
  initialWidth: 100,
  cellRenderer: ({ node, context }: ICellRendererParams) => {
    if (node.group) {
      return <></>;
    }

    const side: SideEnum = column.params?.side;
    const baseCurrency: string = column.params?.baseCurrencyField
      ? get(node.data, column.params.baseCurrencyField)
      : undefined;
    const quoteCurrency: string = column.params?.quoteCurrencyField
      ? get(node.data, column.params.quoteCurrencyField)
      : undefined;
    const marketAccountName: string = column.params?.marketAccountField
      ? get(node.data, column.params.marketAccountField)
      : undefined;

    if (!baseCurrency || !quoteCurrency || !marketAccountName || !side || quoteCurrency === baseCurrency) {
      return <></>;
    }

    const symbol = `${baseCurrency}-${quoteCurrency}`;
    const security: Security | undefined = context.current?.securitiesBySymbol?.get(symbol);

    const marketAccount: MarketAccount | undefined = context.current?.marketAccountsByName?.get(marketAccountName);
    const marketAccountSupported = marketAccount && security && security.Markets?.includes(marketAccount.Market);

    const disabled = security == null || !marketAccountSupported;

    const handleClick = () => column.params?.onClick?.(security, marketAccountName, side, node);

    return (
      <Button onClick={handleClick} size={FormControlSizes.Small} disabled={disabled}>
        {side} {baseCurrency}
      </Button>
    );
  },
});

export const connectionStatus: ColDefFactory<Column> = column => ({
  ...iconButton(column),
  headerName: (typeof column.title === 'string' && column.title) || '',
  valueGetter: ({ node }) => get(node?.data || {}, column.field || ''),
  comparator: (a, b) =>
    typeof a === 'string' && typeof b === 'string' ? stringColumnComparator(a, b) : numericColumnComparator(a, b),
  cellRendererParams: ({ context, node }: ICellRendererParams): AgGridIconButtonProps => {
    if (!context.current.theme) {
      throw new Error('Connection Status column requires theme in context');
    }
    const status = get(node.data, column.field || '') as
      | ConnectionStatusEnum
      | ConnectionModeEnum
      | ModeEnum
      | true
      | false;
    let color = context.current.theme.colorTextMuted;
    switch (status) {
      case true:
        color = context.current.theme.colorTextPositive;
        break;
      case false:
        color = context.current.theme.colorTextNegative;
        break;
      case ConnectionModeEnum.Up:
      case ConnectionStatusEnum.Online:
      case ModeEnum.Enabled:
        color = context.current.theme.colorTextPositive;
        break;
      case ConnectionModeEnum.Down:
      case ConnectionStatusEnum.Disabled:
      case ConnectionStatusEnum.Offline:
      case ModeEnum.Disabled:
        color = context.current.theme.colorTextMuted;
        break;
      case ConnectionStatusEnum.Starting:
      case ConnectionStatusEnum.Stopping:
        color = context.current.theme.colorTextWarning;
        break;
      case ConnectionStatusEnum.Error:
      case ConnectionStatusEnum.Stale:
      case ConnectionStatusEnum.Unavailable:
      case ConnectionStatusEnum.NotInSession:
      case ConnectionStatusEnum.Keepalive:
        color = context.current.theme.colorTextNegative;
        break;
      default:
        color = context.current.theme.colorTextMuted;
    }
    return {
      icon: IconName.DotSolid,
      color: color,
      onClick: () => {},
      dataValue: String(status),
    };
  },
});

/*
Special columns
 */

// Escape hatch for truly custom columns
export const custom: ColDefFactory<Column<ColDef>> = column => ({
  ...baseColumn(column),
  ...column.params,
});

export interface RemoveParams {
  onClick?: (props: { node: RowNode; api: GridApi }) => void;
  allowOnlyForAddedRows?: boolean;
  disabled?: (data: any) => boolean;
  disabledTooltip?: string;
}

export const remove: ColDefFactory<Column<RemoveParams>> = ({ ...column }) => ({
  ...baseColumn(column),
  colId: 'remove',
  cellRenderer: 'iconButton',
  headerClass: 'no-handle',
  headerName: '',
  suppressColumnsToolPanel: true,
  suppressFiltersToolPanel: true,
  suppressHeaderMenuButton: true,
  initialWidth: 40,
  resizable: false,
  tooltipValueGetter: ({ data }) => (column.params?.disabled?.(data) ? column.params.disabledTooltip : undefined),
  tooltipComponent: 'customTooltip',
  cellRendererParams: ({ context, node, api }: ICellRendererParams<RemoveParams>) => {
    if (!node.id) {
      return;
    }
    const row = (context.current as FormBlotterTableContextProps).getRow(node.id);
    if (!row) {
      return;
    }
    if (column.params?.allowOnlyForAddedRows && row.status !== FormRowStatus.Added) {
      return { disabled: true };
    }

    return {
      icon: IconName.Trash,
      onClick: ({ node }: { node: RowNode<FormRowGridItem<unknown>> }) => {
        if (!node.data) {
          return;
        }
        if (node.data.formRow.status !== FormRowStatus.Removed) {
          row.remove();
          column.params?.onClick?.({ node, api });
        }
      },
      disabled: column.params?.disabled?.(node.data),
    };
  },
});

/** @deprecated - use toggle which handles the same behavior */
export const mode: ColDefFactory<Column<ToggleParams>> = column => ({
  ...toggle(column),
  headerClass: 'ag-center-aligned-cell',
  valueGetter: ({ node }) =>
    column.field && (get(node?.data, column.field) === 'Enabled' || get(node?.data, column.field) === true),
  valueSetter: params => {
    if (column.field == null) {
      return false;
    }
    if (typeof params.data[column.field] === 'boolean') {
      params.data[column.field] = params.newValue;
    } else {
      params.data[column.field] = params.newValue ? 'Enabled' : 'Disabled';
    }
    return true;
  },
});

interface ModeWithMessageCellRendererParams extends ICellRendererParams<any, boolean> {
  checked: boolean;
  icon?: ReactNode;
  onChange: (value: boolean) => void;
  tooltip?: ReactNode;
}

export const modeWithMessage: ColDefFactory<Column> = column => ({
  ...mode(column),
  headerName: '',
  headerClass: 'ag-left-aligned-header',
  initialWidth: 80,
  cellRenderer: ({
    checked,
    icon = <Icon icon={IconName.ExclamationCircleSolid} color="colors.yellow.lighten" />,
    onChange,
    tooltip,
    ...props
  }: ModeWithMessageCellRendererParams) => {
    return (
      <HStack gap="spacing8">
        <AgGridToggle {...props} value={checked} setValue={onChange} />
        {tooltip && (
          <Tooltip tooltip={tooltip} usePortal={true}>
            {icon}
          </Tooltip>
        )}
      </HStack>
    );
  },
  cellRendererParams: (params: ICellRendererParams<any, boolean>) => {
    const cellRendererParams = column.params?.cellRendererParams?.(params) ?? {};
    return {
      ...cellRendererParams,
      disabled: column.params?.isDisabled?.(params.data) ?? false,
      onChange: (value: boolean) => {
        params?.setValue?.(value);
        cellRendererParams?.onChange?.(value);
      },
    };
  },
});

export const active: ColDefFactory<Column> = column => ({
  ...toggle(column),
  headerClass: 'ag-center-aligned-cell',
  valueGetter: ({ node }) => column.field && node?.data[column.field] === 'Active',
  valueSetter: params => {
    if (column.field == null) {
      return false;
    }
    params.data[column.field] = params.newValue ? 'Active' : 'Inactive';
    return true;
  },
});

export const formError: ColDefFactory<Column> = column => ({
  ...baseColumn,
});

export const json: ColDefFactory<Column> = column => ({
  ...baseColumn({ ...column, exportable: false }),
  initialWidth: 60,
  cellRenderer: function JSONCellRenderer() {
    return (
      <span style={{ flex: 1, justifyContent: 'flex-start' }}>
        <Icon icon={IconName.Braces} style={{ width: '5px' }} />
      </span>
    );
  },
  valueGetter: ({ data }) => JSON.stringify(data),
  valueFormatter: ({ data }) => JSON.stringify(data, null, 2),
  tooltipComponent: 'customTooltip',
});

/** Cell renderer for bitmap-like json:
 * ```
 *  {[key: string]: boolean}
 * ```
 * which renders keys that have truthy values. */
export const bitmap: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueGetter: ({ data }) => {
    const field = column.field;
    if (!field) {
      return '';
    }
    const bitmap = data[field];
    if (isObject(bitmap)) {
      return getTypedKeys(bitmap)
        .filter(key => bitmap[key])
        .join(', ');
    }
    return '';
  },
});

export const loanParticipant: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueGetter: ({ data, context, colDef }) => {
    const value = colDef.field != null ? data[colDef.field] : null;
    return context.current.loanParticipantsByName?.get(value)?.DisplayName ?? value;
  },
});

export const exposureStatus: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  cellRenderer: ({ data, value }: ICellRendererParams) => {
    if (value) {
      return (
        // Exposure Status shouldn't show the yellow indicator
        <ExposureStatus status={value} text={data?.Status === ExposureStatusEnum.Offline ? data?.Text : null} />
      );
    }
    return null;
  },
  initialWidth: 125,
});

export const bps: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueGetter: ({ data }: ValueGetterParams) => {
    if (!data) {
      return '';
    }
    return percentToBps(data[column.field!]);
  },
  cellRenderer: ({ data, value }: ICellRendererParams) => {
    if (!data || !value) {
      return '';
    }
    return <InlineFormattedNumber number={value} currency="BPS" />;
  },
});

export const pricingAggregation: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueFormatter: ({ context, value }: ValueFormatterParams) => {
    if (!value) {
      return '';
    }
    return context.current.aggregationsByName?.get(value)?.DisplayName ?? value;
  },
});

export const feeMode: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  valueFormatter: ({ value }: ValueFormatterParams) => {
    if (!value) {
      return '';
    }
    return prettyName(value);
  },
});

type RevisionData = { Revision?: string };
export const revision: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  suppressHeaderMenuButton: true,
  sortable: true,
  suppressMovable: true,
  headerValueGetter: () => 'Revision Indicator',
  initialPinned: 'left',
  headerClass: 'hide-header-label',
  maxWidth: 32,
  minWidth: 32,
  valueGetter: ({ data }: ValueGetterParams<RevisionData>) => {
    if (!data) {
      return '';
    }
    if (data.Revision && data.Revision?.toString() !== '0') {
      return 'M';
    }

    return '';
  },
  cellRenderer: ({ value }: ICellRendererParams<RevisionData, string>) => {
    if (value && value === 'M') {
      return (
        <JITTooltip tooltip="The revision of this trade indicates that it has been amended">
          <HStack gap="spacing2">
            <IndicatorBadge square size={IndicatorBadgeSizes.Small} variant={IndicatorBadgeVariants.Modified}>
              M
            </IndicatorBadge>
          </HStack>
        </JITTooltip>
      );
    }
    return null;
  },
});

export const extendedDuration: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  comparator: extendedDurationComparator,
  valueFormatter: ({ value }: ValueFormatterParams<unknown, DateFnInput>) => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    return prettyExtendedDuration(value);
  },
  sortingOrder: ['desc', 'asc'],
});
